import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { MessengerService } from './../../shared/services/messenger.service';
import {
	SendMessage,
	SetUserMessages,
	ArchiveMessage,
	MarkMessagesRead,
	MarkMessageUnread,
	setMessageHeader,
} from './../actions/message-center.actions';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, forkJoin, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { TxApiService } from '../../shared/services/txapi.service';
import { UserInfoService } from '../../shared/services/user-info.service';
import { FetchMessages, FetchMessagesFailed, SetMessages } from '../actions/message-center.actions';
import { ApiResponse } from '../entities/Api/ApiResponse';
import { MessageEntity } from '../entities/MessageEntity';
import { MessageHeadEntity } from '../entities/Api/MessageHeadEntity';
import { State } from '../reducers';
import { catchError, filter } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class MessageCenterEffects {
	getMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FetchMessages),
			filter(({ employee_id }) => employee_id !== null),
			mergeMap(({ employee_id, include_archived }) =>
				this.api
					.callAPI('getMessages', {
						personal_office_for: employee_id,
						archived: include_archived,
					})
					.pipe(
						map((res: ApiResponse<MessageEntity[]>) => {
							const children = res.ListOfMessage.filter((m) => m.parent_id);
							const messages = res.ListOfMessage.filter((m) => m.parent_id === null).map((m) => {
								const c = children.filter((c) => c.parent_id === m.id);
								return MessageEntity.tryCast(m, c);
							});
							return employee_id === 0 ? SetUserMessages(messages) : SetMessages(messages);
						}),
						// Not keepAlive() here: callers wait for a terminal action after dispatching
						// FetchMessages, so swallowing the failure to EMPTY would hang their resolver.
						catchError((err) => {
							console.error('[MessageCenterEffects] getMessages failed:', err);
							return of(FetchMessagesFailed());
						})
					)
			)
		)
	);

	markMessagesRead$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MarkMessagesRead),
			filter(({ messageIds }) => messageIds.length > 0),
			mergeMap(({ personal_office_for, messageIds }) =>
				forkJoin(
					messageIds.map((id) =>
						this.api.callAPI('updateMessage', {
							message_id: id,
							personal_office_for,
							delete_message: false,
							set_read: true,
							set_archived: false,
							set_unread: false,
						})
					)
				).pipe(
					// The badge reads unread_messages from the head, so it only clears once we refetch it.
					switchMap(() => this.api.callAPI('getMessageHead')),
					// Two different badges read two different counters: the personal office badge uses
					// unread_messages from the head, the employee's own badge uses user.unread_messages
					// from getUserInfo. Refresh both so the count clears in whichever view was used.
					// force_data_loading has to be set: without it the service answers NO CONTENT when
					// nothing else changed, and then it skips the setUnreadMessageCount dispatch entirely,
					// so the employee's badge would keep its stale value.
					tap(() => this.userInfo.catchUserInfoFromServer(false, true)),
					map((res: ApiResponse<MessageHeadEntity[]>) =>
						setMessageHeader(res.MessageHead.map((m) => MessageHeadEntity.tryCast(m)))
					),
					this.keepAlive('markMessagesRead')
				)
			)
		)
	);

	markMessageUnread$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MarkMessageUnread),
			mergeMap(({ personal_office_for, messageId }) =>
				this.api
					.callAPI('updateMessage', {
						message_id: messageId,
						personal_office_for,
						delete_message: false,
						set_read: false,
						set_archived: false,
						set_unread: true,
					})
					.pipe(
						switchMap(() => this.api.callAPI('getMessageHead')),
						// Same as in markMessagesRead$: the office badge reads the head, the employee's
						// own badge reads user.unread_messages, and force_data_loading is required or the
						// service gets NO CONTENT back and skips the setUnreadMessageCount dispatch.
						tap(() => this.userInfo.catchUserInfoFromServer(false, true)),
						map((res: ApiResponse<MessageHeadEntity[]>) =>
							setMessageHeader(res.MessageHead.map((m) => MessageHeadEntity.tryCast(m)))
						),
						this.keepAlive('markMessageUnread')
					)
			)
		)
	);

	sendMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SendMessage),
			switchMap(({ message, receiver_id, personal_office_for, parent }) =>
				this.api
					.callAPI('createMessage', {
						type: null,
						type_value: null,
						target_persnr: receiver_id,
						parent_id: parent?.id,
						message,
					})
					.pipe(map(() => FetchMessages(personal_office_for, false)), this.keepAlive('createMessage'))
			),
			tap(() => this.toast.message(this.i18n.instant('toast.message_success'), 'success'))
		)
	);

	archiveMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(ArchiveMessage),
				switchMap(({ id, personal_office_for, archive, remove }) =>
					this.api
						.callAPI('updateMessage', {
							message_id: id,
							personal_office_for: personal_office_for,
							delete_message: remove,
							set_read: false,
							set_archived: true,
							// Required by the UpdateMessage contract. Leaving it out makes WCF fail to
							// deserialize the request and answer 400, which broke archiving entirely.
							set_unread: false,
						})
						.pipe(
							map((res: ApiResponse<any>) => {
								// Only statuscode 0 means success (see TxApiService.dispatchRequestQueue);
								// every other code is an error and must not trigger the refetch.
								if (res.statuscode === 0) {
									this.store.dispatch(FetchMessages(personal_office_for, archive));
								} else {
									this.toast.message(res.statustext, 'error');
								}
							}),
							this.keepAlive('archiveMessage')
						)
				)
			),
		{ dispatch: false }
	);

	constructor(
		private readonly actions$: Actions,
		private readonly api: TxApiService,
		private readonly toast: MessengerService,
		private readonly store: Store<State>,
		private readonly i18n: TranslateService,
		private readonly userInfo: UserInfoService
	) {}

	/**
	 * Swallows the error of a SINGLE request so it cannot escape the effect.
	 *
	 * Background: NgRx subscribes the store as an observer of the effects. If an error
	 * escapes an effect, NgRx calls store.error() and the global actions stream is dead
	 * for good. From that moment on every dispatch() is silently discarded, no effect and
	 * no reducer runs anymore, and every route with a resolver hangs in ResolveStart —
	 * the app looks frozen without ever reporting an error.
	 *
	 * Must therefore sit INSIDE the request pipe, not in the outer actions$ pipe, because
	 * there catchError would terminate the effect stream just the same.
	 */
	private keepAlive<T>(context: string) {
		return catchError<T, Observable<never>>((err) => {
			console.error(`[MessageCenterEffects] ${context} failed:`, err);
			return EMPTY;
		});
	}
}
